import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Phone, MessageCircle, Mail, Clock, ArrowRight, Car, Plane, MapPin } from 'lucide-react';
import PageSEO from '@/components/PageSEO';
import { BUSINESS } from '@/lib/constants';
import { contactPage, breadcrumb } from '@/lib/schemas';

const HERO_IMG = 'https://images.pexels.com/photos/10762406/pexels-photo-10762406.jpeg?auto=compress&cs=tinysrgb&w=1400';

const CONTACT_METHODS = [
  {
    icon: Phone,
    title: 'Call',
    desc: 'Fastest way to get a ride.',
    value: BUSINESS.phone,
    href: BUSINESS.phoneTel,
    external: false,
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    desc: 'Message us — great for sending pickup details.',
    value: '718.400.8989',
    href: BUSINESS.whatsappUrl,
    external: true,
  },
  {
    icon: Mail,
    title: 'Email',
    desc: 'Best for non-urgent questions and feedback.',
    value: BUSINESS.email,
    href: BUSINESS.emailMailto,
    external: false,
  },
];

const QUICK_LINKS = [
  { label: 'Request a Ride', icon: ArrowRight, to: '/request-a-ride' },
  { label: 'Local Rides', icon: Car, to: '/local-rides-staten-island' },
  { label: 'Airport Rides', icon: Plane, to: '/airport-rides-staten-island' },
  { label: 'Service Area', icon: MapPin, to: '/staten-island-service-area' },
];

const FAQS = [
  {
    q: 'How do I book a ride?',
    a: 'Call, WhatsApp, or submit a ride request online. Ride requests are confirmed after Island Ride reviews timing, availability, and fare.',
  },
  {
    q: 'What are your hours?',
    a: 'Island Ride is available 24/7, every day of the week.',
  },
  {
    q: 'Do you serve my area?',
    a: null,
  },
];

// Social accounts — hidden until real URLs are provided
const SOCIAL_ACCOUNTS = [
  // { platform: 'Facebook', url: '', icon: 'facebook' },
  // { platform: 'Instagram', url: '', icon: 'instagram' },
];

const CONTACT_SCHEMAS = [
  contactPage(),
  breadcrumb('Contact', '/contact'),
];

export default function Contact() {
  return (
    <>
      <PageSEO
        title="Contact Island Ride — Staten Island Cab & Car Service"
        description="Contact Island Ride for Staten Island taxi and car service. Call 718.400.8989, WhatsApp, or email. Available 24/7."
        path="/contact"
        schemas={CONTACT_SCHEMAS}
      />
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="contact-hero">
        <img
          src={HERO_IMG}
          alt="Verrazzano-Narrows Bridge at sunset with Staten Island Ferry — contact Island Ride for local cab service, airport rides, and out-of-town travel across Staten Island"
          title="Contact Island Ride — Staten Island cab service"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-background/40 to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <h1
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary"
            data-testid="contact-h1"
          >
            Contact Island Ride
          </h1>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-lg leading-relaxed">
            Call, WhatsApp, or email Island Ride for local Staten Island rides, airport trips, and out-of-town travel. Sedans, minivans, and SUVs available.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild data-testid="contact-hero-call">
              <a href={BUSINESS.phoneTel}><Phone className="h-4 w-4" /><span className="ml-1.5">{BUSINESS.phone}</span></a>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="contact-hero-whatsapp">
              <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /><span className="ml-1.5">WhatsApp</span></a>
            </Button>
            <Button asChild variant="outline" className="bg-background/80" data-testid="contact-hero-email">
              <a href={BUSINESS.emailMailto}><Mail className="h-4 w-4" /><span className="ml-1.5">Email</span></a>
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Methods + About */}
      <section className="bg-secondary border-y" data-testid="contact-info-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
            {/* Left — Contact Details */}
            <div>
              <h2
                className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
                data-testid="contact-methods-heading"
              >
                Get in touch
              </h2>
              <div className="mt-6 space-y-5">
                {CONTACT_METHODS.map((m) => (
                  <div key={m.title} className="flex items-start gap-4" data-testid={`contact-method-${m.title.toLowerCase()}`}>
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-background border text-primary shrink-0">
                      <m.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-display text-sm font-bold text-foreground">{m.title}</p>
                      <p className="text-sm text-muted-foreground">{m.desc}</p>
                      <a
                        href={m.href}
                        className="mt-1 inline-block text-sm font-medium text-primary pretty-link break-all"
                        {...(m.external ? { target: '_blank', rel: 'noreferrer' } : {})}
                        data-testid={`contact-link-${m.title.toLowerCase()}`}
                      >
                        {m.value}
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              {/* Hours */}
              <div className="mt-6 flex items-start gap-4" data-testid="contact-hours">
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-background border text-primary shrink-0">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-display text-sm font-bold text-foreground">Hours</p>
                  <p className="text-sm text-muted-foreground">{BUSINESS.hours}</p>
                </div>
              </div>

              {/* Service area note */}
              <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
                Island Ride is a service-area cab company serving Staten Island. We do not operate a public walk-in office. To request a ride, call, WhatsApp, or use our{' '}
                <Link to="/request-a-ride" className="text-primary font-medium hover:underline">online request form</Link>.
              </p>

              {/* Social — hidden until accounts provided */}
              {SOCIAL_ACCOUNTS.length > 0 && (
                <div className="mt-6 flex gap-3">
                  {SOCIAL_ACCOUNTS.map((s) => (
                    <a key={s.platform} href={s.url} target="_blank" rel="noreferrer" className="text-primary hover:text-primary/80">
                      {s.platform}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Right — About */}
            <div>
              <h2
                className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
                data-testid="about-heading"
              >
                About Island Ride
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Island Ride is a local Staten Island cab service for everyday rides, airport trips, and out-of-town travel. We serve families, commuters, and travelers across the island, with pre-installed child seats available to make family rides easier.
              </p>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                Call, WhatsApp, or request a ride online. Sedans, minivans, and SUVs available.
              </p>
              <div className="mt-6">
                <Button asChild data-testid="about-cta-request">
                  <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="quick-links-section"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="quick-links-heading"
          >
            Looking for something else?
          </h2>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="flex items-center gap-3 rounded-xl bg-background border p-4 transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md"
                data-testid={`quick-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-primary shrink-0">
                  <link.icon className="h-4 w-4" />
                </div>
                <span className="font-display text-sm font-semibold leading-tight">{link.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="section-pad"
        style={{ background: 'linear-gradient(to right, rgba(16,97,95,0.06), rgba(16,97,95,0))' }}
        data-testid="contact-faq-section"
      >
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          <h2
            className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary"
            data-testid="contact-faq-heading"
          >
            Frequently asked questions
          </h2>
          <div className="mt-8 max-w-3xl">
            <Accordion type="single" collapsible className="space-y-2" data-testid="contact-faq">
              {FAQS.map((faq) => (
                <AccordionItem key={faq.q} value={faq.q} className="rounded-lg border bg-background px-5">
                  <AccordionTrigger
                    className="text-sm font-semibold text-left py-4 hover:no-underline"
                    data-testid={`contact-faq-trigger-${faq.q.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                    {faq.a ? faq.a : (
                      <>
                        Island Ride accepts ride requests across Staten Island and is especially active around central and east-side neighborhoods. Visit our{' '}
                        <Link to="/staten-island-service-area" className="text-primary font-medium hover:underline">Service Area page</Link>
                        {' '}or call/WhatsApp us to check availability.
                      </>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Bottom CTA Strip */}
      <section className="bg-primary text-primary-foreground" data-testid="contact-bottom-cta">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-y-3 gap-x-5 text-center">
            <span className="font-display font-semibold text-sm sm:text-[15px]">Need a ride?</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="contact-cta-call">
                <a href={BUSINESS.phoneTel}><Phone className="h-3.5 w-3.5" /><span className="ml-1.5">Call {BUSINESS.phone}</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="contact-cta-whatsapp">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5" /><span className="ml-1.5">WhatsApp</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="contact-cta-request">
                <Link to="/request-a-ride">Request a ride<ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
